import { useCallback, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { getRootNavComponentDimensionAction } from '../NavLayoutDimensions/actions';
import setRootStyleProperty from '../utils/setRootStyleProperty';
import { GLOBAL_NAVIGATION_TOOLBAR_DEFAULT_HEIGHT, GLOBAL_NAVIGATION_SIDEBAR_DEFAULT_COLLAPSED_WIDTH } from 'nav-meta/constants';
/**
 * Hook for programmatically controlling navigation visibility
 * Properly handles both Redux state and CSS variables for layout
 */
export const useNavigationVisibility = () => {
  var _toolbarDimensions$he4, _verticalNavDimension4;
  const dispatch = useAppDispatch();
  const storedDimensionsRef = useRef(null);

  // Get current dimensions from Redux state
  const toolbarDimensions = useAppSelector(state => state.navLayoutDimensionsState.rootNav.GlobalToolbar);
  const verticalNavDimensions = useAppSelector(state => state.navLayoutDimensionsState.rootNav.VerticalNav);

  // Store current dimensions when they're visible (non-zero)
  useEffect(() => {
    var _toolbarDimensions$he, _verticalNavDimension;
    const toolbarHeight = (_toolbarDimensions$he = toolbarDimensions.height) !== null && _toolbarDimensions$he !== void 0 ? _toolbarDimensions$he : 0;
    const verticalNavWidth = (_verticalNavDimension = verticalNavDimensions.width) !== null && _verticalNavDimension !== void 0 ? _verticalNavDimension : 0;
    if (toolbarHeight > 0 || verticalNavWidth > 0) {
      storedDimensionsRef.current = {
        toolbarHeight: toolbarHeight > 0 ? toolbarHeight : parseInt(GLOBAL_NAVIGATION_TOOLBAR_DEFAULT_HEIGHT, 10),
        verticalNavWidth: verticalNavWidth > 0 ? verticalNavWidth : parseInt(GLOBAL_NAVIGATION_SIDEBAR_DEFAULT_COLLAPSED_WIDTH, 10)
      };
    }
  }, [toolbarDimensions.height, verticalNavDimensions.width]);
  const setNavigationVisibility = useCallback(options => {
    const {
      isToolbarVisible,
      isVerticalNavVisible
    } = options;
    const stored = storedDimensionsRef.current;
    const toolbarHeight = isToolbarVisible ? (stored === null || stored === void 0 ? void 0 : stored.toolbarHeight) || parseInt(GLOBAL_NAVIGATION_TOOLBAR_DEFAULT_HEIGHT, 10) : 0;
    const verticalNavWidth = isVerticalNavVisible ? (stored === null || stored === void 0 ? void 0 : stored.verticalNavWidth) || parseInt(GLOBAL_NAVIGATION_SIDEBAR_DEFAULT_COLLAPSED_WIDTH, 10) : 0;
    dispatch(getRootNavComponentDimensionAction({
      compName: 'GlobalToolbar',
      dimensions: {
        height: toolbarHeight
      }
    }));
    dispatch(getRootNavComponentDimensionAction({
      compName: 'VerticalNav',
      dimensions: {
        width: verticalNavWidth
      }
    }));
    setRootStyleProperty('--global-nav-height', `${toolbarHeight}px`);
    setRootStyleProperty('--uicomponents-global-nav-height', `${toolbarHeight}px`);
    setRootStyleProperty('--global-nav-vertical-nav-width', `${verticalNavWidth}px`);
  }, [dispatch]);
  const hideToolbar = useCallback(() => {
    var _verticalNavDimension2;
    const verticalNavWidth = (_verticalNavDimension2 = verticalNavDimensions.width) !== null && _verticalNavDimension2 !== void 0 ? _verticalNavDimension2 : 0;
    setNavigationVisibility({
      isToolbarVisible: false,
      isVerticalNavVisible: verticalNavWidth > 0
    });
  }, [setNavigationVisibility, verticalNavDimensions.width]);
  const showToolbar = useCallback(() => {
    var _verticalNavDimension3;
    const verticalNavWidth = (_verticalNavDimension3 = verticalNavDimensions.width) !== null && _verticalNavDimension3 !== void 0 ? _verticalNavDimension3 : 0;
    setNavigationVisibility({
      isToolbarVisible: true,
      isVerticalNavVisible: verticalNavWidth > 0
    });
  }, [setNavigationVisibility, verticalNavDimensions.width]);
  const hideVerticalNav = useCallback(() => {
    var _toolbarDimensions$he2;
    const toolbarHeight = (_toolbarDimensions$he2 = toolbarDimensions.height) !== null && _toolbarDimensions$he2 !== void 0 ? _toolbarDimensions$he2 : 0;
    setNavigationVisibility({
      isToolbarVisible: toolbarHeight > 0,
      isVerticalNavVisible: false
    });
  }, [setNavigationVisibility, toolbarDimensions.height]);
  const showVerticalNav = useCallback(() => {
    var _toolbarDimensions$he3;
    const toolbarHeight = (_toolbarDimensions$he3 = toolbarDimensions.height) !== null && _toolbarDimensions$he3 !== void 0 ? _toolbarDimensions$he3 : 0;
    setNavigationVisibility({
      isToolbarVisible: toolbarHeight > 0,
      isVerticalNavVisible: true
    });
  }, [setNavigationVisibility, toolbarDimensions.height]);
  const hideAllNavigation = useCallback(() => {
    setNavigationVisibility({
      isToolbarVisible: false,
      isVerticalNavVisible: false
    });
  }, [setNavigationVisibility]);
  const showAllNavigation = useCallback(() => {
    setNavigationVisibility({
      isToolbarVisible: true,
      isVerticalNavVisible: true
    });
  }, [setNavigationVisibility]);
  const restoreNavigationVisibility = useCallback(() => {
    const stored = storedDimensionsRef.current;
    if (stored) {
      setNavigationVisibility({
        isToolbarVisible: stored.toolbarHeight > 0,
        isVerticalNavVisible: stored.verticalNavWidth > 0
      });
    } else {
      // Fallback to showing both if no stored dimensions
      showAllNavigation();
    }
  }, [setNavigationVisibility, showAllNavigation]);
  const toolbarHeight = (_toolbarDimensions$he4 = toolbarDimensions.height) !== null && _toolbarDimensions$he4 !== void 0 ? _toolbarDimensions$he4 : 0;
  const verticalNavWidth = (_verticalNavDimension4 = verticalNavDimensions.width) !== null && _verticalNavDimension4 !== void 0 ? _verticalNavDimension4 : 0;
  const currentVisibility = {
    isToolbarVisible: toolbarHeight > 0,
    isVerticalNavVisible: verticalNavWidth > 0
  };
  return {
    isToolbarVisible: currentVisibility.isToolbarVisible,
    isVerticalNavVisible: currentVisibility.isVerticalNavVisible,
    currentVisibility,
    setNavigationVisibility,
    hideToolbar,
    showToolbar,
    hideVerticalNav,
    showVerticalNav,
    hideAllNavigation,
    showAllNavigation,
    restoreNavigationVisibility
  };
};